import React from "react";
import { useDebounce } from "use-debounce";
import { RnChevronRight } from "@remi/icons/next";
import { isTruthy } from "@remi/ts-utils";

import { useMedia } from "../../hooks";
import type { OptionData, OptionSection } from "../../types/option-data";
import { cn, filterList, getRaw, type VerboseOptionData } from "../../utils";
import { RemiButton } from "../RemiButton";
import { RemiCollapsible, RemiCollapsibleContent } from "../RemiCollapsible";
import { RemiPopover, RemiPopoverContent, RemiPopoverTrigger } from "../RemiPopover";
import {
  DropdownCommand,
  DropdownCommandEmpty,
  DropdownCommandGroup,
  DropdownCommandInput,
  DropdownCommandList,
  DropdownCommandSeparator,
} from "./DropdownCommand";
import {
  DropdownModal,
  DropdownModalBody,
  DropdownModalContent,
  DropdownModalFooter,
  DropdownModalHeader,
  DropdownModalTrigger,
} from "./DropdownModal";
import { SelectItem } from "./SelectItem";

interface SelectOptionProps<T extends string> extends React.HTMLAttributes<HTMLDivElement> {
  option: VerboseOptionData<T>;
  setSelectedValue: (value: T | null) => void;
  selectedValue: T | null;
  setParentOpen: (open: boolean) => void;
  search?: boolean;
  layer?: number;
  canDeselect?: boolean;
  selectedIcon?: React.ReactNode;
  itemSize?: "sm" | "md" | "lg" | "auto";
}

function SelectOption<T extends string>({
  option,
  setParentOpen,
  setSelectedValue,
  selectedValue,
  search = true,
  layer = 0,
  canDeselect = false,
  selectedIcon,
  itemSize,
}: SelectOptionProps<T>) {
  const { isMobile } = useMedia();

  const [open, setOpen] = React.useState<boolean>(false);
  const [debouncedOpen] = useDebounce(open, 200);

  const setOpenPanel = (open: boolean) => {
    setOpen(open);
    setParentOpen(open);
  };

  const selected = !!selectedValue && option.hasChildren !== true && selectedValue === option.value;

  const handleMouseEnter = () => {
    if (option.hasChildren === true) {
      setOpen(true);
    }
  };
  const handleMouseLeave = () => {
    if (option.hasChildren === true) {
      setOpen(false);
    }
  };
  const selectItem = () => {
    const display = option.render ? option.render() : option.label;
    const plainRow =
      option.render || itemSize === "auto" ? (
        display
      ) : (
        <span className="truncate" title={option.label}>
          {option.label}
        </span>
      );
    return (
      <SelectItem
        appearance="standard"
        selectedIcon={selectedIcon}
        size={itemSize}
        key={option.hasChildren !== true ? option.value : `${option.parent}-${option.label}`}
        value={option.hasChildren !== true ? option.value : `${option.parent}-${option.label}`}
        icon={option.parent === undefined || layer !== 0 ? option.icon : undefined}
        selected={selected}
        expandable={option.hasChildren}
        disabled={option.disabled}
        hidden={option.hidden}
        hint={option.badge}
        id={option.id}
        onSelect={() => {
          if (option.hasChildren !== true && option.onClick) {
            option.onClick(option.value);
          }
          if (option.hasChildren !== true) {
            if (selected && canDeselect) {
              setSelectedValue(null);
            } else {
              setSelectedValue(option.value);
              setOpenPanel(false);
            }
          } else {
            setOpen(true);
          }
        }}
      >
        {option.parent && layer === 0 ? (
          <span className="flex min-w-0 items-center gap-1">
            <span className="truncate text-text-tertiary" title={option.parent}>
              {option.parent}
            </span>
            <RnChevronRight className="shrink-0 text-icon-tertiary" size="xs" aria-hidden="true" />
            {isTruthy(option.icon) && <span className="shrink-0">{option.icon}</span>}
            <span className="truncate text-text-secondary" title={typeof display === "string" ? display : undefined}>
              {display}
            </span>
          </span>
        ) : (
          plainRow
        )}
      </SelectItem>
    );
  };
  const selectList = () => {
    return (
      <SelectList
        setOpen={setOpenPanel}
        setSelectedValue={setSelectedValue}
        selectedValue={selectedValue}
        data={option.hasChildren === true ? option.options : []}
        search={search}
        layer={layer + 1}
      />
    );
  };

  if (!isMobile) {
    return (
      <RemiPopover open={debouncedOpen} onOpenChange={setOpen}>
        <RemiPopoverTrigger asChild onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          {selectItem()}
        </RemiPopoverTrigger>
        <RemiPopoverContent
          className="-mt-[5px] ml-1"
          side="right"
          align="start"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {selectList()}
        </RemiPopoverContent>
      </RemiPopover>
    );
  }
  return (
    <DropdownModal open={open} onOpenChange={setOpen}>
      <DropdownModalTrigger asChild>{selectItem()}</DropdownModalTrigger>
      <DropdownModalContent>
        <DropdownModalHeader>{option.label}</DropdownModalHeader>
        <DropdownModalBody>{selectList()}</DropdownModalBody>
        <DropdownModalFooter>
          <RemiButton
            fullWidth
            className="h-full justify-start"
            variant="tertiary"
            size="mobile"
            onClick={() => setOpen(false)}
          >
            Back
          </RemiButton>
        </DropdownModalFooter>
      </DropdownModalContent>
    </DropdownModal>
  );
}

interface SelectListProps<T extends string> extends React.HTMLAttributes<HTMLDivElement> {
  selectedValue: T | null;
  setSelectedValue: (value: T | null) => void;
  actions?: React.ReactNode;
  actionPosition?: "top" | "bottom";
  data: readonly OptionData<T>[];
  sections?: readonly OptionSection<T>[];
  setOpen: (open: boolean) => void;
  search?: boolean;
  onSearch?: (element: HTMLInputElement) => void;
  hasReset?: boolean;
  resetLabel?: string;
  fill?: boolean;
  layer?: number;
  canDeselect?: boolean;
  selectedIcon?: React.ReactNode;
  itemSize?: "sm" | "md" | "lg" | "auto";
}

function SelectList<T extends string>({
  selectedValue,
  setSelectedValue,
  setOpen,
  data,
  sections,
  search = true,
  onSearch,
  actions,
  actionPosition = "bottom",
  hasReset = false,
  resetLabel = "Reset",
  fill = false,
  layer = 0,
  canDeselect = false,
  selectedIcon,
  itemSize,
  ...props
}: SelectListProps<T>) {
  const { isMobile } = useMedia();

  const flatData = React.useMemo(
    () => (sections === undefined ? data : sections.flatMap((section) => section.options)),
    [sections, data],
  );
  const [filteredData, setFilteredData] = React.useState<readonly VerboseOptionData<T>[]>(flatData);
  const [queryActive, setQueryActive] = React.useState(false);

  const onFilter = ({ value }: HTMLInputElement) => {
    const rawValue = getRaw(value);
    setQueryActive(rawValue !== "");
    if (!rawValue) {
      setFilteredData(flatData);
    } else {
      setFilteredData(filterList(flatData, value));
    }
  };

  const hasActions = (): boolean => {
    return actions !== undefined || hasReset;
  };

  const onInput = (element: HTMLInputElement) => {
    if (search) {
      onFilter(element);
      if (onSearch) {
        onSearch(element);
      }
    }
  };

  const renderOption = (option: VerboseOptionData<T>) => (
    <SelectOption
      key={option.hasChildren !== true ? option.value : `${option.parent}-${option.label}`}
      option={option}
      setSelectedValue={setSelectedValue}
      selectedValue={selectedValue}
      setParentOpen={setOpen}
      search={false}
      layer={layer}
      canDeselect={canDeselect}
      selectedIcon={selectedIcon}
      itemSize={itemSize}
    />
  );

  // With the search input hidden nothing inside is tabbable, so the popover would keep focus above cmdk's key handler.
  return (
    <DropdownCommand shouldFilter={false} tabIndex={search ? -1 : 0} className={cn(fill && "sm:w-full")}>
      <RemiCollapsible open={hasActions()}>
        <DropdownCommandInput
          isMobile={isMobile}
          onChangeCapture={(e: React.ChangeEvent<HTMLInputElement>) => onInput(e.target)}
          hidden={!search}
          placeholder="Search"
        />
        {isTruthy(actions) && actionPosition === "top" && (
          <>
            <DropdownCommandGroup>{actions}</DropdownCommandGroup>
            {filteredData.length > 0 && <DropdownCommandSeparator />}
          </>
        )}
        <DropdownCommandList {...props}>
          <DropdownCommandEmpty>No results</DropdownCommandEmpty>
          {sections !== undefined && !queryActive ? (
            sections.map((section) => (
              <DropdownCommandGroup key={section.key} heading={section.heading}>
                {section.options.map(renderOption)}
              </DropdownCommandGroup>
            ))
          ) : (
            <DropdownCommandGroup>{filteredData.map(renderOption)}</DropdownCommandGroup>
          )}
        </DropdownCommandList>
        <RemiCollapsibleContent>
          {isTruthy(actions) && actionPosition === "bottom" && (
            <>
              {filteredData.length > 0 && <DropdownCommandSeparator />}
              <DropdownCommandGroup>{actions}</DropdownCommandGroup>
            </>
          )}
          {hasReset && selectedValue && (
            <>
              <DropdownCommandSeparator />
              <DropdownCommandGroup>
                <SelectItem variant="destructive" onSelect={() => setSelectedValue(null)}>
                  {resetLabel}
                </SelectItem>
              </DropdownCommandGroup>
            </>
          )}
        </RemiCollapsibleContent>
      </RemiCollapsible>
    </DropdownCommand>
  );
}

export { SelectList };
